'use client';

import { c as _c } from "react/compiler-runtime";
import { DatePicker } from '../../components';
import { FieldShell } from './FieldShell';
import { getFieldIds } from './utils';
import type { FieldValues } from 'react-hook-form';
import type { DatePickerProps } from '../../components/DatePicker';
import type { BaseFieldProps } from './types';
export type DateStorageFormat = 'iso' | 'date-only';
export type DatePickerComponentProps = Omit<DatePickerProps, 'onSelect' | 'selected'> & {
  /**
   * The format to use when storing the date value in the form.
   * - 'iso': Full ISO string (e.g., "2024-01-15T00:00:00.000Z")
   * - 'date-only': Date only in YYYY-MM-DD format (e.g., "2024-01-15")
   * @default 'iso'
   */
  storageFormat?: DateStorageFormat;
  /**
   * The earliest selectable month. Accepts Date or ISO string.
   * Explicitly typed here since it's part of the DatePickerProps union.
   */
  startMonth?: Date | string;
  /**
   * The latest selectable month. Accepts Date or ISO string.
   * Explicitly typed here since it's part of the DatePickerProps union.
   */
  endMonth?: Date | string;
  /**
   * Alternative to startMonth - the earliest selectable year (e.g., 2020).
   * Used by backend APIs. Converted to startMonth internally.
   */
  fromYear?: number;
  /**
   * Alternative to endMonth - the latest selectable year (e.g., 2030).
   * Used by backend APIs. Converted to endMonth internally.
   */
  toYear?: number;
};
interface DatePickerFieldProps<TFieldValues extends FieldValues> extends BaseFieldProps<TFieldValues> {
  componentProps: DatePickerComponentProps;
}

/**
 * Formats a Date object to the specified storage format.
 */
function formatDateForStorage(date: Date, format: DateStorageFormat): string {
  if (format === 'date-only') {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  return date.toISOString();
}

/**
 * Converts year-based props (fromYear/toYear) to Date-based props (startMonth/endMonth).
 * The backend may send fromYear/toYear as numbers, but DatePicker expects startMonth/endMonth as Dates.
 */
function normalizeYearProps(props: DatePickerComponentProps) {
  const {
    fromYear,
    toYear,
    startMonth,
    endMonth,
    ...rest
  } = props;
  return {
    ...rest,
    // Prefer explicit startMonth/endMonth if provided, otherwise convert fromYear/toYear
    startMonth: startMonth ?? (fromYear !== undefined ? new Date(fromYear, 0, 1) : undefined),
    endMonth: endMonth ?? (toYear !== undefined ? new Date(toYear, 11, 31) : undefined)
  };
}
export function DatePickerField(t0) {
  const $ = _c(26);
  const {
    field,
    fieldConfig,
    fieldState,
    componentProps,
    labelSize
  } = t0;
  let isDisabled;
  let restComponentProps;
  let storageFormat;
  let t1;
  if ($[0] !== componentProps || $[1] !== field.disabled || $[2] !== fieldConfig || $[3] !== fieldState) {
    const {
      id,
      disabled: componentDisabled,
      storageFormat: t2,
      ...t3
    } = normalizeYearProps(componentProps);
    restComponentProps = t3;
    storageFormat = t2 === undefined ? "iso" : t2;
    isDisabled = field.disabled || componentDisabled;
    t1 = getFieldIds(fieldConfig, fieldState, id);
    $[0] = componentProps;
    $[1] = field.disabled;
    $[2] = fieldConfig;
    $[3] = fieldState;
    $[4] = isDisabled;
    $[5] = restComponentProps;
    $[6] = storageFormat;
    $[7] = t1;
  } else {
    isDisabled = $[4];
    restComponentProps = $[5];
    storageFormat = $[6];
    t1 = $[7];
  }
  const ids = t1;
  let t2;
  if ($[8] !== field.value) {
    t2 = field.value ? new Date(typeof field.value === "string" && !field.value.includes("T") ? `${field.value}T00:00:00` : field.value) : undefined;
    $[8] = field.value;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const dateValue = t2;
  let t3;
  if ($[10] !== field || $[11] !== storageFormat) {
    t3 = date => {
      field.onChange(date ? formatDateForStorage(date, storageFormat) : "");
    };
    $[10] = field;
    $[11] = storageFormat;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleSelect = t3;
  let t4;
  if ($[13] !== dateValue || $[14] !== handleSelect || $[15] !== ids.describedBy || $[16] !== ids.isInvalid || $[17] !== isDisabled || $[18] !== restComponentProps) {
    t4 = <DatePicker {...restComponentProps} className="w-full" aria-describedby={ids.describedBy} aria-invalid={ids.isInvalid} disabled={isDisabled} mode="single" onSelect={handleSelect} selected={dateValue} />;
    $[13] = dateValue;
    $[14] = handleSelect;
    $[15] = ids.describedBy;
    $[16] = ids.isInvalid;
    $[17] = isDisabled;
    $[18] = restComponentProps;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  let t5;
  if ($[20] !== fieldConfig || $[21] !== fieldState || $[22] !== ids || $[23] !== labelSize || $[24] !== t4) {
    t5 = <FieldShell fieldConfig={fieldConfig} fieldState={fieldState} ids={ids} labelSize={labelSize}>{t4}</FieldShell>;
    $[20] = fieldConfig;
    $[21] = fieldState;
    $[22] = ids;
    $[23] = labelSize;
    $[24] = t4;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  return t5;
}